import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type {
  ActivityType,
  ActivityTypeInput,
  Crew,
  CrewInput,
  LabourProvider,
  LabourProviderInput,
  Worker,
  WorkerInput,
  WorkerType,
} from '../types/fieldWork';

// ============================================================================
// FIELD WORK SERVICE — /api/field-work
// Activity types (any farm_jobs seat reads, admin edits), the worker roster and
// crews (supervisor/creator/admin), labour providers (creator/admin edit).
// ============================================================================

class FieldWorkService {
  // --------------------------------------------------------------------------
  // Activity types
  // --------------------------------------------------------------------------

  async getActivityTypes(includeInactive = false): Promise<ActivityType[]> {
    const res = await apiService.get<ActivityType[]>(
      `/field-work/activity-types${includeInactive ? '?include_inactive=true' : ''}`,
    );
    return unwrap(res) ?? [];
  }

  async createActivityType(input: ActivityTypeInput): Promise<ActivityType> {
    const res = await apiService.post<ActivityType>('/field-work/activity-types', input);
    return unwrap(res);
  }

  async updateActivityType(id: number, input: Partial<ActivityTypeInput>): Promise<ActivityType> {
    const res = await apiService.patch<ActivityType>(`/field-work/activity-types/${id}`, input);
    return unwrap(res);
  }

  // --------------------------------------------------------------------------
  // Workers
  // --------------------------------------------------------------------------

  async getWorkers(filters: {
    include_inactive?: boolean;
    worker_type?: WorkerType;
    labour_provider_id?: number;
    crew_id?: number;
    search?: string;
  } = {}): Promise<Worker[]> {
    const params = new URLSearchParams();
    if (filters.include_inactive) params.append('include_inactive', 'true');
    if (filters.worker_type) params.append('worker_type', filters.worker_type);
    if (filters.labour_provider_id) params.append('labour_provider_id', String(filters.labour_provider_id));
    if (filters.crew_id) params.append('crew_id', String(filters.crew_id));
    if (filters.search) params.append('search', filters.search);
    const qs = params.toString();
    const res = await apiService.get<Worker[]>(`/field-work/workers${qs ? `?${qs}` : ''}`);
    return unwrap(res) ?? [];
  }

  /** A scanned or typed picker code → the worker, or null when nobody has it. */
  async findWorkerByCode(code: string): Promise<Worker | null> {
    try {
      const res = await apiService.get<Worker>(`/field-work/workers/by-code/${encodeURIComponent(code.trim())}`);
      return unwrap(res) ?? null;
    } catch (e: any) {
      if (e?.status === 404 || e?.response?.status === 404) return null;
      throw e;
    }
  }

  async createWorker(input: WorkerInput): Promise<Worker> {
    const res = await apiService.post<Worker>('/field-work/workers', input);
    return unwrap(res);
  }

  async updateWorker(id: number, input: Partial<WorkerInput>): Promise<Worker> {
    const res = await apiService.patch<Worker>(`/field-work/workers/${id}`, input);
    return unwrap(res);
  }

  async addWorkersFromUsers(userIds: number[], workerType: WorkerType = 'employee'): Promise<{ created: Worker[]; skipped: number }> {
    const res = await apiService.post<{ created: Worker[]; skipped: number }>(
      '/field-work/workers/from-users',
      { user_ids: userIds, worker_type: workerType },
    );
    return unwrap(res);
  }

  /** Picker cards PDF (A4, ten per page). Empty ids = every active worker. */
  async getPickerCardsPdf(ids: number[] = []): Promise<Blob> {
    const qs = ids.length ? `?ids=${ids.join(',')}` : '';
    return apiService.get(`/field-work/workers/cards.pdf${qs}`, { responseType: 'blob' }) as unknown as Promise<Blob>;
  }

  // --------------------------------------------------------------------------
  // Labour providers
  // --------------------------------------------------------------------------

  async getProviders(includeInactive = false): Promise<LabourProvider[]> {
    const res = await apiService.get<LabourProvider[]>(
      `/field-work/labour-providers${includeInactive ? '?include_inactive=true' : ''}`,
    );
    return unwrap(res) ?? [];
  }

  async createProvider(input: LabourProviderInput): Promise<LabourProvider> {
    const res = await apiService.post<LabourProvider>('/field-work/labour-providers', input);
    return unwrap(res);
  }

  async updateProvider(id: number, input: Partial<LabourProviderInput>): Promise<LabourProvider> {
    const res = await apiService.patch<LabourProvider>(`/field-work/labour-providers/${id}`, input);
    return unwrap(res);
  }

  // --------------------------------------------------------------------------
  // Crews
  // --------------------------------------------------------------------------

  async getCrews(includeInactive = false): Promise<Crew[]> {
    const res = await apiService.get<Crew[]>(`/field-work/crews${includeInactive ? '?include_inactive=true' : ''}`);
    return unwrap(res) ?? [];
  }

  async createCrew(input: CrewInput): Promise<Crew> {
    const res = await apiService.post<Crew>('/field-work/crews', input);
    return unwrap(res);
  }

  async updateCrew(id: number, input: Partial<CrewInput>): Promise<Crew> {
    const res = await apiService.patch<Crew>(`/field-work/crews/${id}`, input);
    return unwrap(res);
  }
}

const fieldWorkService = new FieldWorkService();
export default fieldWorkService;
